import { LoanParams } from 'app/models/loan-params'
import {
  submitForApproval,
  deleteSelectedLoan,
  approveSelectedLoan,
  rejectSelectedLoan,
} from 'app/state/ducks/loans/thunks'
import { saveLoanAmendment, deleteDraftLoanAmendment, submitForApprovalAmendment, rejectSubmittedLoanAmendment, approveSubmittedLoanAmendment } from 'app/state/ducks/loan-amendments/thunks'
import {
  ErrorNotification,
  SuccessNotification,
} from 'app/views/components/notifications/notification'
import { Dispatch } from 'redux'

// get action type from amendment type
function getActionType(amendmentType: string | undefined | null): string {
  switch (amendmentType) {
    case 'LoanEdit':
      return 'Loan Edit'
    case 'LoanAmendment':
      return 'Loan Amendment'
    default:
      return 'Loan Amendment'
  }
}

export async function submitLoan(loan: LoanParams, dispatch: Dispatch<any>) {
  const response: any = await dispatch(submitForApproval(loan))
  if (response.success) {
    SuccessNotification({
      title: 'Successful Submission',
      message: 'Loan has been Submitted for Approval',
    })
  } else {
    ErrorNotification({
      title: 'Submission Failed',
      message: response.payload ?? 'Loan submission failed',
    })
  }
  return
}

export async function rejectLoan(loan: LoanParams, dispatch: Dispatch<any>) {
  const response: any = await dispatch(rejectSelectedLoan(loan))
  if (response.success) {
    SuccessNotification({
      title: 'Successful Rejection',
      message: 'Loan has been moved back to a Draft',
    })
  } else {
    ErrorNotification({
      title: 'Rejection Failed',
      message: response.payload ?? 'Loan rejection failed',
    })
  }
  return
}

export async function approveLoan(loan: LoanParams, dispatch: Dispatch<any>) {
  const response: any = await dispatch(approveSelectedLoan(loan))
  if (response.success) {
    SuccessNotification({
      title: 'Successful Approval',
      message: 'Loan has been Approved',
    })
  } else {
    ErrorNotification({
      title: 'Approval Failed',
      message: response.payload,
    })
  }
  return
}

export async function deleteLoan(loan: LoanParams, dispatch: Dispatch<any>) {
  const response: any = await dispatch(deleteSelectedLoan(loan))
  if (response.success === false) {
    ErrorNotification({
      title: 'Loan Deletion Failed',
      message: response.payload,
    })
  } else {
    SuccessNotification({
      title: 'Successful Deletion',
      message: 'Loan has been Deleted',
    })
  }
  return
}

export async function amendLoan(loanAmendmentData: any, existingLoan: any, dispatch: Dispatch<any>) {
  // Check if we're editing an existing draft amendment 
  // Form data has amendmentType + existingLoan has status 'Draft'
  const isEditingDraftAmendment = loanAmendmentData.amendmentType && existingLoan?.status === 'Draft'
  const actionType = getActionType(loanAmendmentData.amendmentType)

  const response: any = await dispatch(saveLoanAmendment(loanAmendmentData, isEditingDraftAmendment ? loanAmendmentData : null))
  if (response.success) {
    SuccessNotification({
      title: isEditingDraftAmendment ? `${actionType} Updated` : `${actionType} Created`,
      message: isEditingDraftAmendment ? `${actionType} has been updated successfully` : `${actionType} has been created successfully`,
    })
  } else {
    ErrorNotification({
      title: isEditingDraftAmendment ? `${actionType} Update Failed` : `${actionType} Creation Failed`,
      message: response.payload ?? (isEditingDraftAmendment ? `${actionType} update failed` : `${actionType} creation failed`),
    })
  }
  return response
}

export async function deleteLoanAmendment(loan: LoanParams, dispatch: Dispatch<any>) {
  const response: any = await dispatch(deleteDraftLoanAmendment([loan.transactionId ?? loan.id ?? ""]))
  const actionType = getActionType(loan.amendmentType)

  if (response.success) {
    SuccessNotification({
      title: `Draft ${actionType} Deleted`,
      message: `Draft ${actionType} has been deleted successfully`,
    })
  } else {
    ErrorNotification({
      title: `Draft ${actionType} Deletion Failed`,
      message: response.payload ?? `Draft ${actionType} deletion failed`,
    })
  }
  return response
}

export async function submitLoanAmendment(loan: LoanParams, dispatch: Dispatch<any>) {
  const actionType = getActionType(loan.amendmentType)

  const response: any = await dispatch(submitForApprovalAmendment(loan))
  if (response.success) {
    SuccessNotification({
      title: 'Successful Submission',
      message: `${actionType} has been submitted for approval`,
    })
  } else {
    ErrorNotification({
      title: 'Submission Failed',
      message: response.payload ?? `${actionType} submission failed`,
    })
  }
  return response
}

export async function rejectLoanAmendment(loan: LoanParams, dispatch: Dispatch<any>) {
  const actionType = getActionType(loan.amendmentType)

  const response: any = await dispatch(rejectSubmittedLoanAmendment(loan))
  if (response.success) {
    SuccessNotification({
      title: 'Successful Rejection',
      message: `${actionType} has been rejected and moved back to draft status`,
    })
  } else {
    ErrorNotification({
      title: 'Rejection Failed',
      message: response.payload ?? `${actionType} rejection failed`,
    })
  }
  return response
}

export async function approveLoanAmendment(loan: LoanParams, dispatch: Dispatch<any>) {
  const actionType = getActionType(loan.amendmentType)

  const response: any = await dispatch(approveSubmittedLoanAmendment(loan))
  if (response.success) {
    SuccessNotification({
      title: 'Successful Approval',
      message: `${actionType} has been approved`,
    })
  } else {
    ErrorNotification({
      title: 'Approval Failed',
      message: response.payload ?? `${actionType} approval failed`,
    })
  }
  return response
}
